import { PREFERENCIAS_PADRAO, escolhaDeMusica, type PreferenciasGaleria } from "./preferencias.js";

/**
 * O link que põe uma TV no ar sem ninguém configurar nada.
 *
 * Quem recebe a TV num evento nem sempre sabe digitar o código, escolher a
 * vivência e ligar as chaves. O link leva tudo, e a galeria aplica sozinha.
 *
 * Tudo no fragmento (`#`), e não na query: o navegador não manda o fragmento ao
 * servidor, então o código não fica no log de acesso do nginx nem vaza no
 * `Referer`. O link vale enquanto o código valer — gerar outro código em
 * `/kiosk` é o que o invalida.
 */
export interface LinkDaGaleria {
  codigo: string;
  /** Nulo: todas as vivências. */
  vivenciaId: string | null;
  /** Só a TV do suporte usa: de qual cliente projetar. */
  donoId: number | null;
  preferencias: PreferenciasGaleria;
}

const bit = (b: boolean) => (b ? "1" : "0");

export function montarLinkDaGaleria(origem: string, cfg: LinkDaGaleria): string {
  const p = new URLSearchParams();
  p.set("codigo", cfg.codigo);
  if (cfg.donoId !== null) p.set("dono", String(cfg.donoId));
  if (cfg.vivenciaId !== null) p.set("vivencia", cfg.vivenciaId);
  p.set("nome", bit(cfg.preferencias.mostrarNome));
  p.set("divulgacoes", bit(cfg.preferencias.divulgacoes));
  if (cfg.preferencias.intervalo !== null) p.set("intervalo", String(cfg.preferencias.intervalo));
  p.set("audio", bit(cfg.preferencias.audio));

  // Música: a playlist pelo id, ou as escolhidas na hora pelos ids, na ordem.
  // Sem música, nada vai — e a ordem e o repetir não teriam o que dizer.
  const m = cfg.preferencias.musica;
  if (m.tipo !== "nenhuma") {
    if (m.tipo === "playlist") p.set("playlist", String(m.id));
    else p.set("musicas", m.ids.join(","));
    p.set("aleatoria", bit(cfg.preferencias.musicaAleatoria));
    p.set("repetir", bit(cfg.preferencias.musicaRepetir));
  }

  return `${origem.replace(/\/+$/, "")}/galeria#${p.toString()}`;
}

/**
 * Lê o fragmento. Sem código válido, não é link de TV: `null`.
 *
 * O resto é tolerante de propósito — um campo faltando ou estragado (link
 * cortado ao colar no WhatsApp, digitado à mão) vira o padrão, e a TV abre do
 * mesmo jeito.
 */
export function lerLinkDaGaleria(hash: string): LinkDaGaleria | null {
  const p = new URLSearchParams(hash.replace(/^#/, ""));

  const codigo = (p.get("codigo") ?? "").trim().toUpperCase();
  if (!/^[A-Z0-9]{4,32}$/.test(codigo)) return null;

  const dono = Number(p.get("dono"));
  const intervalo = Number(p.get("intervalo"));
  const chave = (nome: string, padrao: boolean) => {
    const v = p.get(nome);
    return v === "1" ? true : v === "0" ? false : padrao;
  };

  return {
    codigo,
    vivenciaId: p.get("vivencia")?.trim() || null,
    donoId: Number.isInteger(dono) && dono > 0 ? dono : null,
    preferencias: {
      mostrarNome: chave("nome", PREFERENCIAS_PADRAO.mostrarNome),
      divulgacoes: chave("divulgacoes", PREFERENCIAS_PADRAO.divulgacoes),
      // A mesma faixa do campo "A cada" da galeria.
      intervalo:
        Number.isInteger(intervalo) && intervalo >= 1 && intervalo <= 50 ? intervalo : null,
      audio: chave("audio", PREFERENCIAS_PADRAO.audio),
      musica: p.has("playlist")
        ? escolhaDeMusica({ tipo: "playlist", id: Number(p.get("playlist")) })
        : p.has("musicas")
          ? escolhaDeMusica({
              tipo: "musicas",
              ids: (p.get("musicas") ?? "").split(",").map(Number),
            })
          : { tipo: "nenhuma" },
      musicaAleatoria: chave("aleatoria", PREFERENCIAS_PADRAO.musicaAleatoria),
      musicaRepetir: chave("repetir", PREFERENCIAS_PADRAO.musicaRepetir),
    },
  };
}
